import { createPkcePair } from "./pkce";
import type { ScopedToken } from "./exchange";

// Keeps an embedded session alive across token expiry.
//
// Spectrum issues this frame no refresh token on purpose (embed spec §4.4), so
// there is nothing to refresh WITH: renewal is the handshake again — fresh PKCE
// pair, fresh code from Zurich, fresh redemption. This module owns the timing
// and the correlation; zurichEmbed.ts owns the wiring.
//
// Two triggers, and both are needed:
//
//  - proactive, from `expires_in`: the only one that renews BEFORE anything
//    breaks, so the user never meets a failed request.
//  - reactive, via `request()`: the only one that catches a session the server
//    ended early — a 401, or Spectrum's heartbeat answering
//    `renew_required: true`.
//
// The session in hand stays usable throughout. Nothing is discarded until a
// replacement has actually been redeemed, so a renewal that fails costs nothing
// that was still working.

/**
 * How long before expiry to start — the same 2 minutes the heartbeat uses
 * server-side (`iframe_renew_before_seconds`, renewal spec §5). It covers a
 * worst-case renewal: three 20s waits with 5s and 15s of backoff.
 */
const RENEW_MARGIN_SECONDS = 120;
/** A token already inside the margin — renew almost at once, but not synchronously. */
const MIN_DELAY_MS = 5000;
/** Zurich has this long to answer `sl:renew` before the attempt is abandoned. */
const REPLY_TIMEOUT_MS = 20000;
/** Attempts per renewal before the session is declared unrecoverable. */
const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [5000, 15000];

export type RenewalDeps = {
  /** Emit `sl:renew` to the parent. */
  requestCode: (requestId: string, codeChallenge: string) => void;
  /** Redeem a code against the verifier that matched its challenge. */
  redeem: (authorizationCode: string, codeVerifier: string) => Promise<ScopedToken>;
  /** A replacement token was obtained — refresh the session with it. */
  onRenewed: (token: ScopedToken) => void;
  /** Every attempt failed; the session cannot be saved. */
  onExhausted: (reason: string) => void;
};

export type Renewal = {
  /** Arm the proactive timer from a token's `expires_in`. Call after every success. */
  schedule: (token: ScopedToken) => void;
  /** Ask for a renewal now. Collapses into one already in flight. */
  request: () => void;
  /** A `parent:renew` reply arrived. */
  accept: (requestId: string, authorizationCode: string) => void;
  /** Drop timers and pending state — logout, teardown. */
  stop: () => void;
};

type Pending = {
  requestId: string;
  /** Secret, as in pkce.ts: held here and read once, by the redemption. */
  verifier: string;
  replyTimer: ReturnType<typeof setTimeout>;
};

// crypto.randomUUID needs a secure context, which an embedded app always has,
// but a jsdom test run does not always provide.
const newRequestId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const createRenewal = (deps: RenewalDeps): Renewal => {
  let pending: Pending | null = null;
  // Set synchronously, before the PKCE pair is awaited. `pending` alone cannot
  // gate re-entry: it appears a tick later, so two calls in the same tick would
  // both pass the guard and mint two codes against two challenges — and only
  // the second verifier would survive.
  let inFlight = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let stopped = false;

  const clearTimer = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const clearPending = (): void => {
    if (pending) clearTimeout(pending.replyTimer);
    pending = null;
    inFlight = false;
  };

  const retry = (reason: string): void => {
    clearPending();
    if (stopped) return;
    if (attempts >= MAX_ATTEMPTS) {
      deps.onExhausted(reason);
      return;
    }
    clearTimer();
    timer = setTimeout(request, BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)]);
  };

  const request = (): void => {
    if (stopped || inFlight) return;
    inFlight = true;
    attempts += 1;
    clearTimer();

    // A FRESH pair every attempt. Reusing a challenge would let a code minted
    // for an earlier attempt be redeemed by a later one, which is exactly the
    // binding PKCE exists to create.
    void createPkcePair()
      .then((pkce) => {
        if (stopped) {
          inFlight = false;
          return;
        }
        const requestId = newRequestId();
        pending = {
          requestId,
          verifier: pkce.verifier,
          replyTimer: setTimeout(() => retry("renew_unanswered"), REPLY_TIMEOUT_MS),
        };
        deps.requestCode(requestId, pkce.challenge);
      })
      .catch(() => retry("renew_failed"));
  };

  const accept = (requestId: string, authorizationCode: string): void => {
    // Only the reply to the request currently outstanding. A late answer to an
    // abandoned attempt carries a code bound to a challenge whose verifier is
    // gone: redeeming it can only fail, and would spend a live code doing so.
    if (!pending || pending.requestId !== requestId) return;

    const verifier = pending.verifier;
    // The reply has landed, so the request is over — but the renewal is not, and
    // `inFlight` stays set until the redemption settles. Clearing it here would
    // let a reactive request arriving mid-redemption start a second one.
    clearTimeout(pending.replyTimer);
    pending = null;

    void deps
      .redeem(authorizationCode, verifier)
      .then((token) => {
        if (stopped) return;
        inFlight = false;
        attempts = 0;
        deps.onRenewed(token);
        schedule(token);
      })
      .catch(() => retry("renew_failed"));
  };

  const schedule = (token: ScopedToken): void => {
    clearTimer();
    // No `expires_in` from the server means no honest deadline to plan against.
    // Guessing one risks renewing either far too often or far too late, so the
    // proactive half stays off and the reactive trigger carries renewal alone.
    if (token.expiresIn === undefined || stopped) return;
    const delay = Math.max((token.expiresIn - RENEW_MARGIN_SECONDS) * 1000, MIN_DELAY_MS);
    timer = setTimeout(request, delay);
  };

  const stop = (): void => {
    stopped = true;
    clearTimer();
    clearPending();
  };

  return { schedule, request, accept, stop };
};
